import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { WaitingSettlementsSummary, WaitingAccountCount } from '@/types/accounts';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { toast } from 'sonner';

interface WaitingSettlementsBannerProps {
  summary: WaitingSettlementsSummary;
  loading: boolean;
  loadFailed: boolean;
  onReload: () => void | Promise<void>;
}

/** The bank and masked number of a waiting account, or the translated "an account" when it has no name. */
function useAccountLabel() {
  const t = useTranslations('consultantTax.waiting');
  return (item: Pick<WaitingAccountCount, 'bankName' | 'maskedNumber'>): string => {
    if (!item.bankName) return t('anAccount');
    return item.maskedNumber ? `${item.bankName} ${item.maskedNumber}` : item.bankName;
  };
}

function scrollToAccountRow(bankAccountId: string) {
  const row = document.getElementById(`bank-account-row-${bankAccountId}`);
  if (!row) return;
  row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  row.classList.add('ring-2', 'ring-amber-400');
  setTimeout(() => row.classList.remove('ring-2', 'ring-amber-400'), 2000);
}

function AccountLine({ item, totalCount }: { item: WaitingAccountCount; totalCount: number }) {
  const t = useTranslations('consultantTax.waiting');
  const accountLabel = useAccountLabel();
  const label = accountLabel(item);
  return (
    <div className="flex items-center justify-between gap-2 text-sm text-amber-800">
      <span>{t('line', { label, count: String(item.count) })}</span>
      {totalCount > 0 && (
        <button
          type="button"
          className="text-amber-700 underline underline-offset-2 hover:text-amber-900"
          onClick={() => scrollToAccountRow(item.bankAccountId)}
        >
          {t('map', { label })}
        </button>
      )}
    </div>
  );
}

function RetryBookingLine({ item, onReload }: { item: WaitingAccountCount; onReload: () => void | Promise<void> }) {
  const t = useTranslations('consultantTax.waiting');
  const accountLabel = useAccountLabel();
  const [retrying, setRetrying] = useState(false);
  const label = accountLabel(item);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const result = await ServiceRegistry.getAccountService().retryBooking(item.bankAccountId);
      if (result.succeeded) {
        toast.success(t('retrying'));
      } else {
        // The retry itself errored server-side (logged, not thrown) -- a 200 here is not the
        // same as "it worked" (`.claude/rules/silent-incomplete-success.md`).
        toast.error(t('retryFailed'));
      }
      await onReload();
    } catch {
      // AccountApiClient.retryBooking already reports the server's own message.
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 text-sm text-amber-800">
      <span>
        {t('mappedNotRetried', {
          label,
          hasAccount: item.chartAccountNumber ? 'yes' : 'no',
          account: item.chartAccountNumber ?? '',
          count: item.count,
        })}
      </span>
      <button
        type="button"
        disabled={retrying}
        className="inline-flex items-center gap-1 text-amber-700 underline underline-offset-2 hover:text-amber-900 disabled:opacity-50"
        onClick={handleRetry}
      >
        {retrying && <Loader2 className="h-3 w-3 animate-spin" />}
        {t('retryBooking')}
      </button>
    </div>
  );
}

/**
 * FIN-1021: the /bank page's count-only waiting-settlements banner (S1). Resting state (AC8)
 * renders nothing. EC7: a load failure is its own neutral, visible state -- never silently
 * "nothing waiting". Sits above the "Accounts" heading in AccountsSection.
 */
export default function WaitingSettlementsBanner({ summary, loading, loadFailed, onReload }: WaitingSettlementsBannerProps) {
  const t = useTranslations('consultantTax.waiting');
  if (loading) return null;

  if (loadFailed) {
    return (
      <div
        data-testid="waiting-settlements-load-failed"
        className="flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-muted-foreground"
      >
        {t('loadFailed')}
      </div>
    );
  }

  const totalWaiting = summary.waiting.reduce((sum, a) => sum + a.count, 0);
  const totalBooking = summary.booking.reduce((sum, a) => sum + a.count, 0);
  const totalMappedNotRetried = summary.mappedNotRetried.reduce((sum, a) => sum + a.count, 0);
  const totalRemoved = summary.removed.reduce((sum, a) => sum + a.count, 0);

  const nothingToShow =
    totalWaiting === 0 && totalBooking === 0 && totalMappedNotRetried === 0 &&
    totalRemoved === 0 && summary.entryFailedCount === 0 && summary.cashMethodNeedsReviewCount === 0;

  // AC8: the resting state shows nothing new.
  if (nothingToShow) return null;

  return (
    <div data-testid="waiting-settlements-banner" className="space-y-2">
      {totalBooking > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-lg text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          {t('booking', {
            count: totalBooking,
            banks: summary.booking.map((a) => a.bankName ?? t('anAccount')).join(', '),
          })}
        </div>
      )}

      {totalWaiting > 0 && (
        <div className="space-y-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {t('waitingForMapping', { count: totalWaiting })}
          </div>
          {summary.waiting.map((item) => (
            <AccountLine key={item.bankAccountId} item={item} totalCount={totalWaiting} />
          ))}
        </div>
      )}

      {totalMappedNotRetried > 0 && (
        <div className="space-y-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          {summary.mappedNotRetried.map((item) => (
            <RetryBookingLine key={item.bankAccountId} item={item} onReload={onReload} />
          ))}
        </div>
      )}

      {totalRemoved > 0 && (
        <div className="space-y-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          {summary.removed.map((item) => (
            <div key={item.bankAccountId} className="text-sm text-amber-800">
              {t('removed', { count: item.count })}
            </div>
          ))}
        </div>
      )}

      {summary.entryFailedCount > 0 && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {t('failed', { count: summary.entryFailedCount })}
        </div>
      )}

      {summary.cashMethodNeedsReviewCount > 0 && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {t('needReview', { count: summary.cashMethodNeedsReviewCount })}{' '}
          <a href="/review" className="underline underline-offset-2">
            {t('goToReview')}
          </a>
        </div>
      )}
    </div>
  );
}
